import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faEnvelope, faEye, faRectangleList, faNoteSticky, faClipboardList, faSpinner, faFileMedical, faRepeat } from '@fortawesome/free-solid-svg-icons';
import ActivityModal from '../../detail/tabs/ActivityModal';
import { EntityType } from '../../detail/tabs/ActivityModal';
import CreateMessageModal from '../../detail/tabs/CreateMessageModal';
import ViewMessagesModal from '../../detail/tabs/ViewMessagesModal';
import { EntityType as MessageEntityType } from '../../detail/tabs/ViewMessagesModal';
import CommTemplateManagerModal from '../../detail/tabs/CommTemplateManagerModal';
import NotesTemplateManagerModal from '../NotesTemplateManagerModal';
import ReferralLetterEditorModal from '../ReferralLetterEditorModal';
import { StatusEnum } from '../../enums/statusEnum';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import { TaskTypeEnum } from '../../enums/taskTypeEnum';
import { useNotifications } from '@medprompt/shared';

const PATIENT_NOTE_TYPE = TaskTypeEnum.PATIENT_NOTE.value;

const ActionsTab = ({ consultation, activeUsers, teams = [], patientData, onConsultationUpdate }) => {
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isCreateMessageModalOpen, setIsCreateMessageModalOpen] = useState(false);
  const [isViewMessagesModalOpen, setIsViewMessagesModalOpen] = useState(false);
  const [isTemplateManagerModalOpen, setIsTemplateManagerModalOpen] = useState(false);
  const [isNotesTemplateManagerModalOpen, setIsNotesTemplateManagerModalOpen] = useState(false);
  const [isReferralLetterModalOpen, setIsReferralLetterModalOpen] = useState(false);
  const [templateRefreshTrigger, setTemplateRefreshTrigger] = useState(0);
  const [autoOpenGenerateConfirmation, setAutoOpenGenerateConfirmation] = useState(false);
  const [isWritePatientPlanLoading, setIsWritePatientPlanLoading] = useState(false);
  const [isRegenerateLoading, setIsRegenerateLoading] = useState(false);
  const consultationManager = useMemo(() => new ConsultationService(sharedApiClient), []);
  const { addNotification } = useNotifications();

  const handleOpenActivity = () => {
    setIsActivityModalOpen(true);
  };

  const handleCloseActivity = () => {
    setIsActivityModalOpen(false);
  };

  const handleOpenReferralLetter = () => {
    setIsReferralLetterModalOpen(true);
  };

  const handleCloseReferralLetter = () => {
    setIsReferralLetterModalOpen(false);
  };

  const handleOpenCreateMessage = () => {
    setAutoOpenGenerateConfirmation(false);
    setIsCreateMessageModalOpen(true);
  };

  const handleCloseCreateMessage = () => {
    setIsCreateMessageModalOpen(false);
    setAutoOpenGenerateConfirmation(false);
  };

  const handleRegenerate = useCallback(async () => {
    const consultationId = consultation?.consultationId;
    if (!consultationId) {
      return;
    }
    setIsRegenerateLoading(true);
    try {
      await consultationManager.regenerateConsultation(consultationId);
      addNotification(`regenerate-success-${consultationId}`, 'Consultation queued for regeneration');
    } catch (error) {
      console.error('Error regenerating consultation:', error);
      addNotification(`regenerate-error-${consultationId}`, 'Failed to regenerate consultation', { isError: true });
    } finally {
      setIsRegenerateLoading(false);
    }
  }, [consultation?.consultationId, consultationManager, addNotification]);

  const handleWritePatientPlan = useCallback(async () => {
    const consultationId = consultation?.consultationId;
    const outcomeTasks = consultation?.outcomeTasks;
    if (!onConsultationUpdate || !consultationId || !outcomeTasks?.length) {
      return;
    }
    const patientNoteTasks = outcomeTasks.filter(t => t.taskType === PATIENT_NOTE_TYPE);
    if (patientNoteTasks.length === 0) {
      return;
    }

    setIsWritePatientPlanLoading(true);
    try {
      const toPersist = patientNoteTasks.filter(t => !t.isSelected);
      await Promise.all(
        toPersist.map(task =>
          consultationManager.updateConsultationTask(consultationId, {
            taskId: task.taskId,
            isSelected: true,
            isDeselected: null,
          })
        )
      );

      const updatedTasks = outcomeTasks.map(task =>
        task.taskType === PATIENT_NOTE_TYPE ? { ...task, isSelected: true } : task
      );
      onConsultationUpdate({ ...consultation, outcomeTasks: updatedTasks });
      setAutoOpenGenerateConfirmation(true);
      setIsCreateMessageModalOpen(true);
    } catch (error) {
      console.error('Failed to select patient message tasks:', error);
      addNotification('write-patient-plan-error', 'Failed to update outcome tasks', { isError: true });
    } finally {
      setIsWritePatientPlanLoading(false);
    }
  }, [consultation, consultationManager, onConsultationUpdate, addNotification]);

  const handleOpenViewMessages = () => {
    setIsViewMessagesModalOpen(true);
  };

  const handleCloseViewMessages = () => {
    setIsViewMessagesModalOpen(false);
  };

  const handleOpenTemplateManager = () => {
    setIsTemplateManagerModalOpen(true);
  };

  const handleCloseTemplateManager = () => {
    setIsTemplateManagerModalOpen(false);
    // Increment trigger to force CreateMessageModal to refetch templates
    setTemplateRefreshTrigger(prev => prev + 1);
  };

  const handleOpenNotesTemplateManager = () => {
    setIsNotesTemplateManagerModalOpen(true);
  };

  const handleCloseNotesTemplateManager = () => {
    setIsNotesTemplateManagerModalOpen(false);
  };

  const handleMessageSent = () => {
    // Optionally refresh messages or show notification
    console.log('Message sent successfully');
  };

  // Check if patient demographics are available for sending messages
  const canSendMessage = !!patientData;
  const isConsultationReady = consultation.status === StatusEnum.READY.value;

  const hasPatientNoteTasks = consultation?.outcomeTasks?.some(t => t.taskType === PATIENT_NOTE_TYPE);

  return (
    <div className="details-content">
      <div className="detail-tile">
        <h3 className="tile-header">Consultation</h3>
        <div className="tile-content">
          <button 
            className="button activity-button" 
            onClick={handleOpenActivity}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Open audit trail</span>
            <FontAwesomeIcon icon={faHistory} style={{ fontSize: '18px' }} />
          </button>
          <button 
            className="button activity-button" 
            onClick={handleOpenReferralLetter}
            disabled={!isConsultationReady}
            title={!isConsultationReady ? 'Consultation must be ready' : 'Generate a referral letter from this consultation'}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Write referral letter</span>
            <FontAwesomeIcon icon={faFileMedical} style={{ fontSize: '18px' }} />
          </button>
          <button
            className="button activity-button"
            onClick={handleRegenerate}
            disabled={!isConsultationReady || isRegenerateLoading}
            title={!isConsultationReady ? 'Cannot regenerate in current state' : 'Regenerate Consultation'}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            <span>Regenerate consultation</span>
            {isRegenerateLoading ? (
              <FontAwesomeIcon icon={faSpinner} className="fa-spin" style={{ fontSize: '18px' }} />
            ) : (
              <FontAwesomeIcon icon={faRepeat} style={{ fontSize: '18px' }} />
            )}
          </button>
        </div>
      </div>

      <div className="detail-tile">
        <h3 className="tile-header">Patient Communication</h3>
        <div className="tile-content">
          <button 
            className="button activity-button" 
            onClick={handleOpenViewMessages}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>View sent messages</span>
            <FontAwesomeIcon icon={faEye} style={{ fontSize: '18px' }} />
          </button>
          <button 
            className="button activity-button" 
            onClick={handleOpenCreateMessage}
            disabled={!canSendMessage || !isConsultationReady}
            title={!canSendMessage ? 'Patient record not available' : 'Send message to patient'}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Send message</span>
            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '18px' }} />
          </button>
          {hasPatientNoteTasks && (
            <button
              className="button activity-button"
              onClick={handleWritePatientPlan}
              disabled={
                !canSendMessage ||
                !isConsultationReady ||
                isWritePatientPlanLoading ||
                !onConsultationUpdate
              }
              title={
                !onConsultationUpdate
                  ? 'Unable to update tasks'
                  : !canSendMessage
                    ? 'Patient record not available'
                    : 'Select all patient message tasks and start generating a message'
              }
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '41px',
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '10px',
              }}
            >
              <span>Write patient plan</span>
              {isWritePatientPlanLoading ? (
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" style={{ fontSize: '18px' }} />
              ) : (
                <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: '18px' }} />
              )}
            </button>
          )}
          <button 
            className="button activity-button" 
            onClick={handleOpenTemplateManager}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            <span>Manage message templates</span>
            <FontAwesomeIcon icon={faRectangleList} style={{ fontSize: '18px' }} />
          </button>
        </div>
      </div>

      <div className="detail-tile">
        <h3 className="tile-header">Notes Templates</h3>
        <div className="tile-content">
          <button 
            className="button activity-button" 
            onClick={handleOpenNotesTemplateManager}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            <span>Manage notes templates</span>
            <FontAwesomeIcon icon={faNoteSticky} style={{ fontSize: '18px' }} />
          </button>
        </div>
      </div>

      {isActivityModalOpen && (
        <ActivityModal
          entityType={EntityType.CONSULTATION}
          entityId={consultation.consultationId}
          entity={consultation}
          activeUsers={activeUsers}
          teams={teams}
          onClose={handleCloseActivity}
        />
      )}

      {isReferralLetterModalOpen && (
        <ReferralLetterEditorModal
          consultation={consultation}
          patientData={patientData}
          onClose={handleCloseReferralLetter}
        />
      )}

      {isCreateMessageModalOpen && (
        <CreateMessageModal
          entityType={MessageEntityType.CONSULTATION}
          consultationId={consultation.consultationId}
          patientData={patientData}
          consultation={consultation}
          onClose={handleCloseCreateMessage}
          onMessageSent={handleMessageSent}
          templateRefreshTrigger={templateRefreshTrigger}
          autoOpenGenerateConfirmation={autoOpenGenerateConfirmation}
        />
      )}

      {isViewMessagesModalOpen && (
        <ViewMessagesModal
          entityType={MessageEntityType.CONSULTATION}
          consultationId={consultation.consultationId}
          entityStatus={consultation.status}
          onClose={handleCloseViewMessages}
        />
      )}

      {isTemplateManagerModalOpen && (
        <CommTemplateManagerModal
          onClose={handleCloseTemplateManager}
        />
      )}

      {isNotesTemplateManagerModalOpen && (
        <NotesTemplateManagerModal
          onClose={handleCloseNotesTemplateManager}
        />
      )}
    </div>
  );
};

export default ActionsTab;
